import type { PreferredFileOps} from './fileOps';
import { recursiveMkdir } from './fileOps';
import type { FileSetListener } from './uploadProgression';
import { OperationConclusion } from '../dto/progress';
import { ProgressBuilder } from '../async/progressBuilder';
import throttle from 'lodash/throttle';
import { AsyncIterableBuffer } from '../async/iterableBuffer';
import { invariant } from '../util/invariant';
import type { CancelToken } from 'axios';
import Axios from 'axios';
import type { Logger } from '../types/logger';
import { exceptionToString } from '../util/exception';

/**
 * Data read from the source file that will be written to the destination file.
 * Buffer can be recycled to allow reduce the burden on the garbage collector to
 * reclaim continually allocated buffers.
 */
class DataBuffer {
  buffer: Uint8Array;
  dataSize: number;
  constructor(public blockSize: number) {
    this.buffer = new Uint8Array(blockSize);
    this.dataSize = 0;
  }
}

/**
 * Close a file descriptor, but don't treat a failure as a critical error. E.g.
 * if we've successfully read the full contents of a source file, we can
 * complete creation of the destination copy, even if we can't close the source
 * file. Also, if we have encounter some other critical I/O error during the
 * file copy, we consider that error more significant than being unable to close
 * the file descriptors.
 */
async function tryCloseFd(fileOps: PreferredFileOps, fd: number, logger: Logger) {
  try {
    await fileOps.close(fd);
  } catch (ex) {
    logger.warn(`Non-critical close failure ${fd} ${exceptionToString(ex)}`);
  }
}

/**
 * A file that can be copied to a destination file system location.
 */
export class FileCopySource {
  constructor(private fileOps: PreferredFileOps, private path: string, private cancelToken: CancelToken, private logger: Logger) {
  }

  /**
   * Generate a sequence of buffers by reading from the source file.
   */
  private async* readDataBuffers(recycledBuffers: DataBuffer[], blockSize: number) {
    const readFd = await this.fileOps.open(this.path, 'r');
    try {
      while (true) {
        const data = recycledBuffers.pop() ?? new DataBuffer(blockSize);
        invariant(data, 'free data buffer pool was empty');
        this.cancelToken.throwIfRequested();
        const { bytesRead, buffer } = await this.fileOps.read(readFd, data.buffer, 0, data.blockSize, null);
        if (data.buffer !== buffer) {
          // Hack for Electron: the data is not found in the buffer originally passed, but found in the
          // buffer that is returned. Possibly because the buffer originates in the renderer process but
          // the fs API is accessed via the main process, which is unable to write to the original buffer?
          data.buffer = buffer;
        }

        data.dataSize = bytesRead;
        yield data;
        if (bytesRead == 0) {
          break;
        }
      }
    } finally {
      await tryCloseFd(this.fileOps, readFd, this.logger);
    }
  }

  /**
   * Copy the source file directly to the given destination.
   */
  private async copyTo(dest: string, title: string, itemId: string, knownSize: number, listener: FileSetListener | null) {
    /*
      These block counts and block sizes were determined to be efficient in
      copying directly from P2 cards based on testing at TV2 Fyn.
    */
    const blockCount = 10;
    const blockSize = 0x100000;

    const recycledBuffers: DataBuffer[] = [];
    this.cancelToken.throwIfRequested();

    const progressBuilder = new ProgressBuilder({
      files: {
        done: 0,
        known: 1,
      },
      bytes: {
        done: 0,
        known: knownSize
      },
      status: 'Copying file',
      description: 'Copying ' + title + ' to ' + dest,
      errors: [],
      percentage: 0,
      uploadMethods: { [itemId]: 'fileSystemCopy' },
      conclusion: OperationConclusion.PENDING
    }, listener, this.cancelToken);

    const throttledFireProgress = throttle(progressBuilder.fire.bind(progressBuilder), 100);

    const readBuffers = new AsyncIterableBuffer(blockCount, this.readDataBuffers(recycledBuffers, blockSize));
    readBuffers.startPrefilling();

    let writeFd = -1;
    try {
      writeFd = await this.fileOps.open(dest, 'w');
      for await (const data of readBuffers) {
        let offset = 0;
        while (offset < data.dataSize) {
          this.cancelToken.throwIfRequested();
          const { bytesWritten: written } = await this.fileOps.write(writeFd, data.buffer, offset, data.dataSize - offset);
          offset += written;
          progressBuilder.progress.bytes.done += written;
          throttledFireProgress();
        }
        recycledBuffers.push(data);
        this.cancelToken.throwIfRequested();
      }
      await this.fileOps.close(writeFd);
      writeFd = -1;
      throttledFireProgress.flush();
    } finally {
      throttledFireProgress.cancel();
      const closing = readBuffers.close();
      if (writeFd != -1) {
        await tryCloseFd(this.fileOps, writeFd, this.logger);
      }

      await closing;
    }
  }

  /*
    We copy then rename to mimic the strategy used by Minio. However, if we want
    to add growing file support we may want to change this later.
  */
  async copyToWithMove(dest: string, title: string, itemId: string, knownSize: number, listener: FileSetListener | null): Promise<LocalPathCopyResult> {
    const tmppath = dest + '.part';

    try {
      await recursiveMkdir(this.fileOps, this.fileOps.dirname(dest));
      await this.copyTo(tmppath, title, itemId, knownSize, listener);
    } catch (err) {
      /*
        This is where we need to translate the cancellation exception into a serializable
        object that can be passed across to the renderer, when the copy happens in the main
        process. This is needed because otherwise the exception is unhandled in the main process
        and not proxied to the renderer, which has no way of knowing if the copy was cancelled
        or not.
       */
      if (Axios.isCancel(err)) {
        return {
          wasCancelled: true,
        };
      }

      this.logger.error(`copyToWithMove error: ${exceptionToString(err)}`);
      return {
        wasCancelled: false,
      };
    }

    await this.fileOps.rename(tmppath, dest);
    return {
      wasCancelled: false,
    };
  }
}

export interface LocalPathCopyResult {
  wasCancelled: boolean;
}
export interface CopyParams {
  // A unique ID for each copy operation to track it for cancellation across process boundaries in Desktop.
  executionId: string;
  cancelToken: CancelToken,
  preferred: PreferredFileOps,
  src: string,
  logger: Logger,
  dest: string,
  title: string,
  itemId: string,
  knownSize: number,
  listener: FileSetListener | null
}

export type IpcCopyParams = Omit<CopyParams, 'preferred' | 'cancelToken' | 'listener' | 'logger'>;

export const copyFileOptimizedForP2Cards = async (params: CopyParams) => {
  const sourceFile = new FileCopySource(params.preferred, params.src, params.cancelToken, params.logger);
  return sourceFile.copyToWithMove(params.dest, params.title, params.itemId, params.knownSize, params.listener);
};
